import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { global } from '../global';

/** Fase / mensaje de progreso emitido por la descarga intradía de FMP. */
export interface FmpDescargaProgreso {
  /** Nombre del evento SSE: total | simbolo | progreso | ok | aviso | fin | error */
  tipo: 'total' | 'simbolo' | 'progreso' | 'ok' | 'aviso' | 'fin' | 'error';
  data: string;
}

/**
 * Descarga de velas intradía desde FinancialModelingPrep para mercados que NO son de MetaApi.
 *
 * A diferencia de MetaApi, aquí el navegador no habla con el proveedor: la clave de FMP es de
 * servidor, así que la descarga entera la hace el backend y este servicio solo la dispara y
 * escucha el progreso por SSE.
 *
 * Usa su propia EventSource en vez de SseService.apiGetSse porque aquel tiene los nombres de
 * evento del flujo de creación de modelos cableados y no admite otros.
 */
@Injectable({ providedIn: 'root' })
export class FmpIntradiaService {

  private eventSource: EventSource | null = null;

  /**
   * Lanza la descarga y emite el progreso. Completa al recibir el evento 'fin'.
   *
   * @param idmercado mercado completo, o 0 para descargar un solo activo
   * @param idnectum  activo concreto cuando idmercado es 0
   * @param periodo   id de periodo intradía (1,2,3,4,5,7)
   */
  descargar(idmercado: number, idnectum: number, periodo: number): Observable<FmpDescargaProgreso> {
    const subject = new Subject<FmpDescargaProgreso>();
    const url = `${global.url}fmp/descargarIntradia?idmercado=${idmercado}&idnectum=${idnectum}&periodo=${periodo}`;

    this.cerrar();
    const source = new EventSource(url);
    this.eventSource = source;

    const reenviar = (tipo: FmpDescargaProgreso['tipo']) => {
      source.addEventListener(tipo, (evento: MessageEvent) => {
        subject.next({ tipo, data: evento.data });
        if (tipo === 'fin' || tipo === 'error') {
          source.close();
          if (this.eventSource === source) { this.eventSource = null; }
          subject.complete();
        }
      });
    };

    (['total', 'simbolo', 'progreso', 'ok', 'aviso', 'fin', 'error'] as const).forEach(reenviar);

    source.onerror = () => {
      // El backend cierra el stream al terminar, y el navegador lo reporta como error:
      // solo se propaga si aún no habíamos recibido el 'fin' que completa el Subject.
      source.close();
      if (this.eventSource === source) { this.eventSource = null; }
      subject.complete();
    };

    return subject.asObservable();
  }

  /** Corta una descarga en curso (p. ej. al cambiar de mercado o destruir el componente). */
  cerrar(): void {
    if (this.eventSource) {
      this.eventSource.close();
      this.eventSource = null;
    }
  }
}
